import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { AnimatedCounter, ScrollReveal, SplitReveal } from '@/components/motion';
import { ACHIEVEMENT_HEADLINE } from '@/data/experience';
import { cn } from '@ibas/ui';

/** ACBSP "Accredited Program" badge — permitted here (MBA/DBA achievement content). */
const ACBSP_BADGE = '/media/images/accreditation/acbsp-accredited-program-badge.jpg';

export interface AchievementBandProps {
  className?: string;
  /** Optional id for in-page anchoring. */
  id?: string;
}

/**
 * AchievementBand — the home-page ACBSP Student Achievement band.
 *
 * ACBSP-accredited business schools are required to make student-achievement
 * information publicly available; this band satisfies that on the home page by
 * surfacing the four verified headline figures (retention + graduates) and
 * linking out to the full, mandated Student Achievement Rates page where the
 * complete 2021–2025 series and learning-outcome trends live.
 *
 * SOURCING (ibas-brand): every figure is read verbatim from @/data/experience
 * (ACHIEVEMENT_HEADLINE), which is drawn only from the authoritative ACBSP page —
 * nothing here is invented, and each figure keeps its own year/scope caption.
 * The ACBSP badge appears only alongside this MBA/DBA achievement content.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger for the stat cards,
 * AnimatedCounter for every figure. All primitives are reduced-motion safe and
 * never strand content hidden — under reduced motion the band is a plain,
 * fully legible grid.
 */
export function AchievementBand({ className, id }: AchievementBandProps) {
  return (
    <section
      id={id}
      aria-label="ACBSP student achievement rates"
      className={cn('border-y border-border bg-muted/30 py-16 md:py-24', className)}
    >
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* Heading + lede on the left; ACBSP badge on the right (desktop). */}
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-16">
          <div className="max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Student achievement rates · ACBSP-accredited
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              Accredited outcomes, published in full.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                As an ACBSP-accredited business school, IBAS reports its MBA and DBA student
                achievement openly — retention, graduate growth and learning-outcome results,
                institution-reported for 2021–2025 and published rather than merely claimed.
              </p>
            </ScrollReveal>
          </div>

          <ScrollReveal from="up" delay={0.15} className="hidden lg:block">
            <img
              src={ACBSP_BADGE}
              alt="ACBSP Accredited Program badge."
              width={144}
              height={144}
              loading="lazy"
              className="h-32 w-auto rounded-xl bg-white p-3 shadow-sm ring-1 ring-border"
            />
          </ScrollReveal>
        </div>

        {/* Four headline count-up figures (verbatim from @/data/experience). */}
        <ScrollReveal
          stagger={0.08}
          className="mt-12 grid grid-cols-2 gap-5 sm:gap-6 lg:grid-cols-4"
        >
          {ACHIEVEMENT_HEADLINE.map((stat) => (
            <div
              key={stat.label}
              className="h-full rounded-2xl border border-border bg-background p-6 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0 md:p-7"
            >
              <div className="flex size-11 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <stat.Icon aria-hidden className="size-5" />
              </div>
              <AnimatedCounter
                value={stat.value}
                decimals={stat.decimals ?? 0}
                suffix={stat.suffix}
                grouping
                className="mt-5 block font-display text-4xl leading-none font-semibold text-brand-navy md:text-5xl"
              />
              <p className="mt-3 text-sm leading-snug font-medium text-foreground">{stat.label}</p>
              <p className="mt-1 text-xs leading-snug text-muted-foreground">{stat.caption}</p>
            </div>
          ))}
        </ScrollReveal>

        {/* Provenance + link out to the full mandated page. */}
        <div className="mt-10 flex flex-col gap-3 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Figures are institution-reported for 2021–2025.
          </p>
          <Link
            to="/experience/student-achievement-rates"
            data-cursor
            className="group inline-flex items-center gap-2 text-sm font-medium text-brand-navy transition-colors duration-300 hover:text-brand-gold motion-reduce:transition-none"
          >
            See the full ACBSP figures
            <ArrowRight
              aria-hidden
              className="size-4 transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none"
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
